import type { CreditPackageType, DocumentConfig, VoucherType } from '@alpod-ai-writer/shared';
import { AiFeatureType, CreditTransactionType } from '@alpod-ai-writer/shared';
import { type InferInsertModel, type InferSelectModel,sql } from 'drizzle-orm';
import { boolean, doublePrecision, index, integer, jsonb, pgTable, primaryKey, timestamp, uuid, varchar } from 'drizzle-orm/pg-core';

import type { SecurityMeta } from '../schemas/common/securityMeta.schema';
import type { AITokenUsage } from '../services/ai.lib';
import type { AttributeMap } from '../shared/attributes';
import type { ProviderData } from '../shared/authProvider';
import type { IdentityType } from '../shared/identityTypes.enum';
import type { PrivilegeMap } from '../shared/privileges.enum';

export enum DocumentShareType {
    Read = 'Read',
    Write = 'Write',
}

export enum JobQueueStatus {
    Pending = 'Pending',
    InProgress = 'InProgress',
    Completed = 'Completed',
    Failed = 'Failed',
    CancelRequested = 'CancelRequested',
    Cancelled = 'Cancelled',
}

export enum JobQueueJobType {
    AiProofread = 'AiProofread',
}

const metaColumns = {
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    createdBy: uuid('created_by'),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    updatedBy: uuid('updated_by'),
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
    deletedBy: uuid('deleted_by'),
};

export const IdentitiesTable = pgTable('identities', {
    identityId: uuid('identity_id').notNull().primaryKey().default(sql`uuid_generate_v4()`),
    email: varchar('email', { length: 255 }).notNull(),
    name: varchar('name', { length: 255 }).notNull(),
    type: varchar('type', { length: 255 }).notNull().$type<IdentityType>(),
    avatarUrl: varchar('avatar_url', { length: 1024 }),
    attributes: jsonb('attributes').notNull().$type<AttributeMap>(),
    adminFields: jsonb('system_fields').notNull().$type<AttributeMap>(),
    userFields: jsonb('user_fields').notNull().$type<AttributeMap>(),
    privileges: jsonb('app_privileges').notNull().$type<PrivilegeMap>(),
    systemPrivileges: jsonb('privileges').notNull().$type<PrivilegeMap>(),
    isEnabled: boolean('is_enabled').notNull().default(true),
    lastLoginAt: timestamp('last_login_at', { withTimezone: true }),
    tokensValidAfter: timestamp('tokens_valid_after', { withTimezone: true }).notNull().defaultNow(),
    ...metaColumns
});

export const AuthMethodsTable = pgTable('auth_methods', {
    provider: varchar('provider', { length: 1024 }).notNull(),
    accountKey: varchar('account_key', { length: 1024 }).notNull(),
    accountLabel: varchar('label', { length: 255 }).notNull(),
    identityId: uuid('identity_id').notNull().references(() => IdentitiesTable.identityId, { onDelete: 'cascade', onUpdate: 'cascade' }),
    data: jsonb('data').notNull().$type<ProviderData>(),
    lastUsedAt: timestamp('last_used_at', { withTimezone: true }),
    ...metaColumns
}, (t) => [
    primaryKey({
        name: 'auth_methods_pkey',
        columns: [ t.provider, t.accountKey ]
    }),
    index('auth_methods_identity_id_idx').on(t.identityId),
]);

export const AuthSessionsTable = pgTable('auth_sessions', {
    sessionId: uuid('session_id').notNull().primaryKey().default(sql`uuid_generate_v4()`),
    identityId: uuid('identity_id').notNull().references(() => IdentitiesTable.identityId, { onDelete: 'cascade', onUpdate: 'cascade' }),
    provider: varchar('provider', { length: 1024 }).notNull(),
    accountKey: varchar('account_key', { length: 1024 }).notNull(),
    label: varchar('label', { length: 255 }).notNull(),
    lastRefreshTokenId: uuid('last_refresh_token_id').notNull(),
    createdSecurityMeta: jsonb('created_security_meta').notNull().$type<SecurityMeta>(),
    lastSecurityMeta: jsonb('last_security_meta').notNull().$type<SecurityMeta>(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    refreshedAt: timestamp('refreshed_at', { withTimezone: true }).notNull().defaultNow(),
    expiresAt: timestamp('expires_at', { withTimezone: true }).notNull(),
    revokedAt: timestamp('revoked_at', { withTimezone: true }),
    revokedBy: uuid('revoked_by'),
}, (t) => [
    index('auth_sessions_identity_id_idx').on(t.identityId),
]);

export const DocumentsTable = pgTable('documents', {
    documentId: uuid('document_id').notNull().primaryKey().default(sql`uuid_generate_v4()`),
    documentName: varchar('document_name', { length: 255 }).notNull(),
    documentType: varchar('document_type', { length: 255 }).notNull(),
    templateId: uuid('template_id').references(() => DocTemplatesTable.templateId, { onDelete: 'restrict', onUpdate: 'cascade' }),
    goal: varchar('goal', { length: 10000 }).notNull().default(''),
    variableSymbol: varchar('variable_symbol', { length: 10 }).unique().notNull().default(sql`generate_variable_symbol()`),
    ownerIdentityId: uuid('owner').notNull(),
    paidForAt: timestamp('paid_for_at', { withTimezone: true }),
    invoiceId: varchar('invoice_id', { length: 32 }),
    ...metaColumns,
}, (t) => [
    index('documents_owner_idx').on(t.ownerIdentityId),
]);

export const CreditBalancesTable = pgTable('credit_balances', {
    ownerIdentityId: uuid('identity_id')
        .primaryKey()
        .references(() => IdentitiesTable.identityId, { onDelete: 'cascade' }),

    balance: integer('balance').notNull().default(0),

    usedCredits: integer('used_credits').notNull().default(0),
    totalSpent: integer('total_spent').notNull().default(0),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (t) => [
    index('credit_balance_identity_id_idx').on(t.ownerIdentityId),
]);

export const CreditPackagesTable = pgTable('credit_packages', {
    packageId: uuid('package_id').defaultRandom().primaryKey(),
    label: varchar('label').notNull(),
    description: varchar('description').notNull(),
    descriptionSk: varchar('description_sk'),
    currency: varchar('currency', { length: 3 }).notNull().default('CZK'),
    price: integer('price').notNull(),
    priceEur: integer('price_eur'),
    credits: integer('credits').notNull(),
    saleBadge: varchar('sale_badge').notNull().default(''),
    isActive: boolean('is_active').default(true).notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
});

export const AiFeaturePricesTable = pgTable('ai_feature_prices', {
    featureType: varchar('feature_type').primaryKey().$type<AiFeatureType>(),
    label: varchar('label').notNull(),
    costPerUnit: integer('cost_per_unit').notNull(),
    unit: varchar('unit').notNull(),
    isActive: boolean('is_active').default(true).notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

export const VouchersTable = pgTable('vouchers', {
    voucherId: uuid('voucher_id').defaultRandom().primaryKey(),
    code: varchar('code', { length: 64 }).notNull().unique(),
    type: varchar('type', { length: 32 }).notNull().$type<VoucherType>(),
    value: integer('value').notNull(),
    description: varchar('description'),
    isActive: boolean('is_active').default(true).notNull(),
    expiresAt: timestamp('expires_at', { withTimezone: true }),
    minPurchaseAmount: integer('min_purchase_amount'),
    maxUsesPerUser: integer('max_uses_per_user'),
    used: integer('used').default(0).notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
});

export type VoucherSelectData = InferSelectModel<typeof VouchersTable>;
export type VoucherInsertData = InferInsertModel<typeof VouchersTable>;

export const VoucherUsagesTable = pgTable('voucher_usages', {
    voucherId: uuid('voucher_id')
        .notNull()
        .references(() => VouchersTable.voucherId, { onDelete: 'cascade' }),
    identityId: uuid('identity_id')
        .notNull()
        .references(() => IdentitiesTable.identityId, { onDelete: 'cascade' }),
    usedCount: integer('used_count').default(0).notNull(),
    lastUsedAt: timestamp('last_used_at', { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
    primaryKey({ columns: [t.voucherId, t.identityId] }),
    index('voucher_usages_identity_id_idx').on(t.identityId),
]);

export type VoucherUsageSelectData = InferSelectModel<typeof VoucherUsagesTable>;
export type VoucherUsageInsertData = InferInsertModel<typeof VoucherUsagesTable>;

export const CreditTransactionsTable = pgTable('credit_transactions', {
    transactionId: uuid('transaction_id').defaultRandom().primaryKey(),

    ownerIdentityId: uuid('identity_id')
        .notNull()
        .references(() => IdentitiesTable.identityId, { onDelete: 'cascade' }),

    type: varchar('type').notNull().$type<CreditTransactionType>(),

    credits: integer('credits').notNull(),
    featureType: varchar('feature_type').$type<AiFeatureType>(),
    description: varchar('description'),

    relatedPackageId: uuid('package_id'),
    relatedPackageType: varchar('package_type').$type<CreditPackageType>(),
    relatedDocumentId: uuid('document_id'),
    relatedPaymentOrderId: uuid('payment_order_id').references(() => PaymentOrdersTable.orderId),

    createdAt: timestamp('created_at').defaultNow().notNull(),
}, (t) => [
    index('credit_tx_identity_id_idx').on(t.ownerIdentityId),
    index('credit_tx_created_at_idx').on(t.createdAt),
    index('credit_tx_payment_order_id_idx').on(t.relatedPaymentOrderId),
]);

export enum PaymentOrderType {
    Document = 'document',
    CreditPurchase = 'credit_purchase',
}

export const PaymentOrdersTable = pgTable('payment_orders', {
    orderId: uuid('order_id').notNull().primaryKey().default(sql`uuid_generate_v4()`),
    orderCode: varchar('order_code', { length: 10 }).unique().notNull(),
    orderType: varchar('order_type', { length: 32 }).notNull().$type<PaymentOrderType>(),
    ownerIdentityId: uuid('owner_identity_id').notNull().references(() => IdentitiesTable.identityId, { onDelete: 'cascade' }),
    relatedDocumentId: uuid('related_document_id'),
    residency: varchar('residency', { length: 2 }).notNull(),
    amount: integer('amount').notNull(),
    currency: varchar('currency', { length: 3 }).notNull().default('CZK'),
    status: varchar('status', { length: 32 }).notNull().default('PENDING'),
    resultText: varchar('result_text', { length: 255 }),
    // GP WebPay
    gpwebpayOrderNumber: varchar('gpwebpay_order_number', { length: 15 }).unique(),
    // GoPay
    gopayPaymentId: varchar('gopay_payment_id', { length: 32 }),
    // Invoice
    invoiceId: varchar('invoice_id', { length: 32 }),
    variableSymbol: varchar('variable_symbol', { length: 10 }),
    paidAt: timestamp('paid_at', { withTimezone: true }),
    // Credit purchase specific
    totalCredits: integer('total_credits'),
    packages: jsonb('packages').$type<Array<{ packageId: string; quantity: number; credits: number; price: number }>>(),
    voucherId: uuid('voucher_id'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    index('payment_orders_owner_idx').on(t.ownerIdentityId),
    index('payment_orders_document_idx').on(t.relatedDocumentId),
    index('payment_orders_gpwebpay_order_idx').on(t.gpwebpayOrderNumber),
]);

export const DocRevisionsTable = pgTable('doc_revisions', {
    revisionId: uuid('revision_id').notNull().primaryKey().default(sql`uuid_generate_v4()`),
    documentId: uuid('document_id').notNull().references(() => DocumentsTable.documentId, { onDelete: 'restrict', onUpdate: 'cascade' }),
    label: varchar('label', { length: 255 }).notNull().default(''),
    content: jsonb('content').notNull().$type<Record<string, unknown>>(),
    isCurrent: boolean('is_current').notNull().default(true),
    sourceRevisionId: uuid('source_revision_id').default(sql`null`),
    ...metaColumns,
}, (t) => [
    index('doc_revisions_document_id_idx').on(t.documentId),
]);

export const DocHistoryTable = pgTable('doc_history', {
    editId: uuid('edit_id').notNull().primaryKey().default(sql`uuid_generate_v4()`),
    documentId: uuid('document_id').notNull(),
    editorUserId: uuid('editor_user_id').notNull(),
    prompt: jsonb('prompt').notNull().$type<Record<string, unknown>>(),
    promptResult: jsonb('prompt_result').notNull().$type<Record<string, unknown>>(),
    documentDiff: jsonb('document_diff').notNull().$type<Record<string, unknown>>(),
    lastEditedAt: timestamp('last_edited_at', { withTimezone: true }).notNull(),
    lastEditedByUserId: uuid('last_edited_by_user_id'),
    ...metaColumns,
});

export const DocSharingTable = pgTable('doc_sharing', {
    documentId: uuid('document_id').notNull().references(() => DocumentsTable.documentId, { onDelete: 'cascade', onUpdate: 'cascade' }),
    identityId: uuid('identity_id').notNull().references(() => IdentitiesTable.identityId, { onDelete: 'cascade', onUpdate: 'cascade' }),
    shareType: varchar('share_type', { length: 255 }).notNull().$type<DocumentShareType>(),
}, (table) => [
    primaryKey({ columns: [table.documentId, table.identityId] })
]);

export const DocTemplatesTable = pgTable('doc_templates', {
    templateId: uuid('template_id').notNull().primaryKey().default(sql`uuid_generate_v4()`),
    label: varchar('label', { length: 255 }).notNull(),
    iconPath: varchar('icon_path', { length: 1024 }).notNull(),
    docxPath: varchar('docx_path', { length: 1024 }).notNull(),
    config: jsonb('config').notNull().$type<DocumentConfig>(),
    isPublished: boolean('is_published').notNull().default(false),
    ...metaColumns,
});

export const JobQueueTable = pgTable('job_queue', {
    jobId: uuid('job_id').notNull().primaryKey().default(sql`uuid_generate_v4()`),
    documentId: uuid('document_id').notNull().references(() => DocumentsTable.documentId, { onDelete: 'restrict', onUpdate: 'cascade' }),
    jobType: varchar('job_type', { length: 255 }).notNull().$type<JobQueueJobType>(),
    status: varchar('status', { length: 255 }).notNull().$type<JobQueueStatus>(),
    params: jsonb('params').notNull().$type<Record<string, unknown>>(),
    progress: jsonb('progress').$type<Record<string, unknown>>(),
    log: jsonb('log').$type<unknown[]>(),
    lastErrorName: varchar('last_error_name', { length: 255 }),
    lastErrorMessage: varchar('last_error_message', { length: 10000 }),
    retryCount: integer('retry_count').notNull().default(0),
    lockedAt: timestamp('locked_at', { withTimezone: true }),
    lockToken: uuid('lock_token'),
    startedAt: timestamp('started_at', { withTimezone: true }),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    dismissedAt: timestamp('dismissed_at', { withTimezone: true }),
    ...metaColumns,
}, (t) => [
    index('job_queue_document_id_idx').on(t.documentId),
    index('job_queue_status_idx').on(t.status),
]);

export const UsageLogTable = pgTable('usage_log', {
    logId: uuid('log_id').notNull().primaryKey().default(sql`uuid_generate_v4()`),
    identityId: uuid('identity_id').notNull().references(() => IdentitiesTable.identityId, { onDelete: 'restrict', onUpdate: 'cascade' }),
    documentId: uuid('document_id').notNull().references(() => DocumentsTable.documentId, { onDelete: 'restrict', onUpdate: 'cascade' }),
    activity: varchar('activity', { length: 255 }).notNull(),
    startedAt: timestamp('started_at', { withTimezone: true }),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    charactersProcessed: integer('characters_processed'),
    context: jsonb('context'),
    errorRate: doublePrecision('error_rate'),
    modelUsage: jsonb('model_usage').$type<Record<string, AITokenUsage>>(),
    toolUsage: jsonb('tool_usage').$type<Record<string, number>>(),
    mainModel: varchar('main_model', { length: 255 }),
}, (t) => [
    index('usage_log_identity_id_idx').on(t.identityId),
    index('usage_log_document_id_idx').on(t.documentId),
]);

/* Interfaces */
export type IdentitySelectData = InferSelectModel<typeof IdentitiesTable>;
export type IdentityInsertData = InferInsertModel<typeof IdentitiesTable>;
export type IdentityUpdateData = Partial<InferInsertModel<typeof IdentitiesTable>>;

export type AuthMethodSelectData = InferSelectModel<typeof AuthMethodsTable>;
export type AuthMethodInsertData = InferInsertModel<typeof AuthMethodsTable>;
export type AuthMethodUpdateData = Partial<InferInsertModel<typeof AuthMethodsTable>>;

export type AuthSessionSelectData = InferSelectModel<typeof AuthSessionsTable>;
export type AuthSessionInsertData = InferInsertModel<typeof AuthSessionsTable>;
export type AuthSessionUpdateData = Partial<InferInsertModel<typeof AuthSessionsTable>>;

export type DocumentSelectData = InferSelectModel<typeof DocumentsTable>;
export type DocumentInsertData = InferInsertModel<typeof DocumentsTable>;
export type DocumentUpdateData = Partial<InferInsertModel<typeof DocumentsTable>>;

export type CreditTransactionsTable = InferSelectModel<typeof CreditTransactionsTable>;

export type DocRevisionSelectData = InferSelectModel<typeof DocRevisionsTable>;
export type DocRevisionInsertData = InferInsertModel<typeof DocRevisionsTable>;
export type DocRevisionUpdateData = Partial<InferInsertModel<typeof DocRevisionsTable>>;

export type DocHistorySelectData = InferSelectModel<typeof DocHistoryTable>;
export type DocHistoryInsertData = InferInsertModel<typeof DocHistoryTable>;
export type DocHistoryUpdateData = Partial<InferInsertModel<typeof DocHistoryTable>>;

export type DocSharingSelectData = InferSelectModel<typeof DocSharingTable>;
export type DocSharingInsertData = InferInsertModel<typeof DocSharingTable>;
export type DocSharingUpdateData = Partial<InferInsertModel<typeof DocSharingTable>>;

export type DocTemplateSelectData = InferSelectModel<typeof DocTemplatesTable>;
export type DocTemplateInsertData = InferInsertModel<typeof DocTemplatesTable>;
export type DocTemplateUpdateData = Partial<InferInsertModel<typeof DocTemplatesTable>>;

export type AIJobQueueSelectData = InferSelectModel<typeof JobQueueTable>;
export type AIJobQueueInsertData = InferInsertModel<typeof JobQueueTable>;
export type AIJobQueueUpdateData = Partial<InferInsertModel<typeof JobQueueTable>>;

export type UsageLogSelectData = InferSelectModel<typeof UsageLogTable>;
export type UsageLogInsertData = InferInsertModel<typeof UsageLogTable>;
export type UsageLogUpdateData = Partial<InferInsertModel<typeof UsageLogTable>>;

export type PaymentOrderSelectData = InferSelectModel<typeof PaymentOrdersTable>;
export type PaymentOrderInsertData = Omit<InferInsertModel<typeof PaymentOrdersTable>, 'orderCode'>;

export const DocumentUnlockSettingsTable = pgTable('document_unlock_settings', {
    id: integer('id').primaryKey().default(1),
    cardTitle: varchar('card_title').notNull().default('Formátování práce (CZ i SK)'),
    cardDescription: varchar('card_description').notNull().default(''),
    priceCZK: integer('price_czk').notNull().default(399),
    priceEUR: integer('price_eur').notNull().default(17),
    benefits: jsonb('benefits').notNull().default(['Plně naformátovaná šablona VŠ', 'Import / export s možností úprav', 'Kontrola formátu práce', '100 % správnost dle nařízení VŠ', 'Průměrný čas zpracování: 4 minuty']).$type<string[]>(),
});

export type DocumentUnlockSettingsSelectData = InferSelectModel<typeof DocumentUnlockSettingsTable>;
export type DocumentUnlockSettingsUpdateData = Partial<Omit<InferInsertModel<typeof DocumentUnlockSettingsTable>, 'id'>>;

export const FioProcessedTransactionsTable = pgTable('fio_processed_transactions', {
    transactionId: varchar('transaction_id', { length: 64 }).notNull(),
    residency: varchar('residency', { length: 8 }).notNull(),
    processedAt: timestamp('processed_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.transactionId, t.residency] }),
]);
